import { Component, Inject, OnInit } from '@angular/core';
import {
  PremiumClient,
  PremiumCalculationSearchRequest,
  CoverageRateResponse,
  PremiumItemResponse,
  ApiResponseOfPremiumCalculationSearchResponse,
  BillingPeriod,
  PremiumCalculatorInfo
} from '@benefitsui/app/api/benefits-clients';
import {
  PremiumCalcAccordionType,
  BandedRateCellType,
  CompositeRateCellType,
  TableHeaderTypes,
  InputValueChangeType,
  CalculationData
} from './premium-calc-types';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';
import { BillingService } from '../billing.service';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { Constants } from '../billing-page.types';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';
import { BillingPageComponent } from '../billing-page.component';
import { environment } from '@benefitsui/environments/environment';
import { EnvName } from 'envName';
import { UserRole } from '@benefitsui/app/shared/enums/users';

declare global {
  interface QsiInterface {
    API: {
      unload: () => void;
      load: () => Promise<void>;
      run: () => PromiseLike<void> | void | null | undefined;
    };
  }

  interface Window {
    QSI?: QsiInterface;
  }
}

@Component({
  selector: 'app-premium-calculation',
  templateUrl: './premium.component.html',
  styleUrls: ['./premium.component.scss'],
  standalone: false
})
export class PremiumComponent implements OnInit {
  //constants
  CloseText = Constants.CloseText;
  InvoiceCardHeader = 'Invoice details';
  MemberMonth = 'member / month';
  ModalBodyError = 'You are not allowed to change premium calculations.';
  ModalTitle = "Oops, you don't have access";
  PremiumCalculatorHeader = 'Premium calculator';
  RateBasis = '1000';
  StatusCardHeader = 'Premium calculation status';
  SubtotalLabel = 'Subtotal: ';

  decimalPipe = new DecimalPipe('en-US');

  TableHeaders: TableHeaderTypes = {
    composite: [
      { text: 'Coverage type', key: 'coverageType', width: '20%' },
      { text: 'Class', key: 'class', width: '15%' },
      { text: 'Number of lives', key: 'numLives', width: '15%' },
      { text: 'Volume', key: 'volume', width: '20%' },
      { text: 'Rate', key: 'rateUI', width: '15%' },
      { text: 'Amounts', key: 'amounts', align: 'right' }
    ],
    banded: [
      { text: 'Step band by age', key: 'stepBand', width: '35%' },
      { text: 'Number of lives', key: 'numLives', width: '15%' },
      { text: 'Volume', key: 'volume', width: '15%' },
      { text: 'Rate', key: 'rateUI', width: '20%' },
      { text: 'Amounts', key: 'amounts', align: 'right' }
    ]
  };

  TotalAmountLabel = 'Total calculated amount';

  constructor(
    private premiumClient: PremiumClient,
    private billingService: BillingService,
    private userContextService: UserContextService,
    private policyService: PolicyService,
    @Inject(WINDOW) private window: Window,
    @Inject(BillingPageComponent) private parent: BillingPageComponent
  ) {
    this.groupName = this.billingService.premiumInvoiceData?.billGroupName || Constants.StringEmpty;
    this.groupNumber = this.billingService.premiumInvoiceData?.billGroupNumber || Constants.StringEmpty;
    this.invoiceNumber = this.billingService.premiumInvoiceData?.invoiceNumber || Constants.StringEmpty;
    this.invoiceKey = this.billingService.premiumInvoiceData?.invoiceKey || Constants.StringEmpty;
    this.premiumKey = this.billingService.premiumInvoiceData?.premiumKey || Constants.StringEmpty;
    this.dueDateKey = this.billingService.premiumInvoiceData?.dueDateKey || Constants.StringEmpty;
    this.startDate = this.billingService.premiumInvoiceData?.startDate || Constants.StringEmpty;
    this.endDate = this.billingService.premiumInvoiceData?.endDate || Constants.StringEmpty;
    this.invoicePeriod = this.billingService.premiumInvoiceData?.invoicePeriod || this.defaultBillingPeriod;
    this.invoicePeriodString = this.billingService.premiumInvoiceData?.invoicePeriodString || Constants.StringEmpty;
    this.premiumId = this.billingService.premiumInvoiceData?.premiumID || Constants.StringEmpty;
    this.saComment = this.billingService.premiumInvoiceData?.saComment || Constants.StringEmpty;
    this.submittedBy = this.billingService.premiumInvoiceData?.submittedBy || Constants.StringEmpty;
    this.submittedDate = this.billingService.premiumInvoiceData?.submittedDate || Constants.StringEmpty;
    this.paymentStatus = null;
    this.invoiceAmount = null;
    this.statusColor = null;

    this.reqParams = {
      groupId: Constants.StringEmpty,
      premiumId: this.premiumId,
      premiumKey: this.premiumKey,
      invoiceKey: this.invoiceKey,
      invoiceNumber: this.invoiceNumber,
      dueDateKey: this.dueDateKey,
      coverageStartDate: this.startDate,
      coverageStopDate: this.endDate
    };

    this.userContext = this.userContextService.get();
    this.userType = this.userContext?.symClass;

    this.interceptLoaded();
  }

  data$!: Observable<PremiumCalcAccordionType[]>;
  groupName: string;
  groupNumber: string;
  invoiceAmount: string | null;
  invoiceNumber: string;
  invoiceKey: string;
  premiumKey: string;
  dueDateKey: string;
  startDate: string;
  endDate: string;
  invoicePeriod: BillingPeriod | undefined;
  isLoading = true;
  paymentStatus: string | null;
  premiumId: string;
  reqParams: PremiumCalculationSearchRequest;
  statusColor: string | null;
  userContext: UserContext;
  userType: string;

  calculationUpdated: CalculationData[] = [];
  calculationOriginal: CalculationData[] = [];
  submittedSuccess = false;

  showPermissionModal = false;
  hasSubmitPermissions = false;
  statePlaceholder = 'Not submitted';
  stateColor = this.getStatusColor(this.statePlaceholder);
  submittedBySA = '--';
  submitDateSA = '--';
  numericCharSet = /[0-9]/;
  numericCharSetWDecimal = /[0-9.]/;
  accordionData: PremiumCalcAccordionType[] = [];
  invoiceTotal = Constants.StringEmpty;
  hasInterceptLoaded = false;

  invoiceLabel = {
    invoicePeriod: 'Invoice period',
    invoiceNumber: 'Invoice number',
    billGroupName: 'Bill group name',
    billGroupNumber: 'Bill group number',
    paymentStatus: 'Payment status',
    estimatedAmount: 'Estimated amount'
  };

  statusLabel = {
    state: 'State',
    submittedBy: 'Changed by',
    submittedDate: 'Changed date'
  };
  defaultBillingPeriod: BillingPeriod = { from: undefined, to: undefined };
  invoicePeriodString: string;
  saComment: string;
  submittedBy: string;
  submittedDate: string;
  private numLivesInput$ = new BehaviorSubject<InputValueChangeType>({
    event: 0,
    tableIndex: 0,
    accordionIndex: 0,
    item: undefined,
    inputType: Defaults.stringEmpty
  });

  textInputType = {
    numLivesStr: 'numLivesStr',
    volumeStr: 'volumeStr'
  };

  private volumeInput$ = new BehaviorSubject<InputValueChangeType>({
    event: 0,
    tableIndex: 0,
    accordionIndex: 0,
    item: undefined,
    inputType: Defaults.stringEmpty
  });

  private readonly debounceTimeMs = 900; // more time for user to type in the decimal value

  // update the number of lives and numLivesStr from the input
  onNumLivesChange(input: InputValueChangeType) {
    // remove existing commas or decimals
    const tempLive = input.item?.numLivesStr?.replace(/[^0-9]/g, '');
    const formattedNumLives = this.decimalPipe.transform(tempLive);
    this.accordionData[input.accordionIndex].cellRows[input.tableIndex].numLivesStr = formattedNumLives;
    this.numLivesInput$.next(input);
  }

  // update the volume and volumeStr from the input
  onVolumeChange(input: InputValueChangeType) {
    //remove any existing commas or decimals
    const initialVolume = input.item?.volumeStr?.replace(/[^0-9]/g, '');
    const volume = this.parsePotentiallyGroupedFloat(initialVolume ?? '0');
    const formattedVolume = this.formatNumber(volume);
    this.accordionData[input.accordionIndex].cellRows[input.tableIndex].volumeStr = formattedVolume;
    this.volumeInput$.next(input);
  }

  ngOnInit(): void {
    const premiumSelfAdminRequest: PremiumCalculationSearchRequest = {
      groupId: '',
      premiumId: this.premiumId ?? '',
      premiumKey: this.premiumKey ?? '',
      invoiceKey: this.invoiceKey ?? '',
      invoiceNumber: this.invoiceNumber ?? '',
      dueDateKey: this.dueDateKey,
      coverageStartDate: this.startDate,
      coverageStopDate: this.endDate
    };
    if (premiumSelfAdminRequest.premiumId === '') {
      this.goBillingList();
    }
    this.data$ = this.premiumClient
      .calculation(premiumSelfAdminRequest)
      ?.pipe(map((response) => this.transformData(response)));
    this.numLivesInput$
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe((inputValue) => this.inputValueChange(inputValue));
    this.volumeInput$
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe((inputValue) => this.inputValueChange(inputValue));
    this.hasSubmitPermissions = this.hasPermissions(this.userContext);
  }

  interceptLoaded() {
    if (this.window.QSI?.API !== undefined) {
      this.hasInterceptLoaded = true;
    } else {
      this.hasInterceptLoaded = false;
    }
  }

  transformData(response: ApiResponseOfPremiumCalculationSearchResponse): PremiumCalcAccordionType[] {
    const accordions: PremiumCalcAccordionType[] = [];
    const compositeCells: CompositeRateCellType[] = [];

    if (this.submittedBy !== '' && this.submittedDate !== '') {
      this.statePlaceholder = 'Submitted';
      this.stateColor = this.getStatusColor(this.statePlaceholder);
      this.submittedBySA = this.saComment;
      this.submitDateSA = this.submittedDate ?? '--';
    }

    const compositeAccordion: PremiumCalcAccordionType = {
      title: 'Composite rate coverages/services',
      subtotal: 0,
      subtotalUI: this.SubtotalLabel,
      tableHeaders: this.TableHeaders.composite,
      cellRows: [],
      showContent: true
    };

    //allows composite accordion to always appear first on the page
    accordions.push(compositeAccordion);
    let compositeSubtotal = 0;

    response?.data?.premiumCalculationInfo?.coverageItems?.map((item) => {
      const volume = item?.compositeRates?.volume;
      const rate = item?.compositeRates?.rate ?? 0;
      const rateBasis = this.RateBasis;
      const numLives = item?.compositeRates?.headCount;
      const rowTotal = item?.compositeRates?.premium ?? 0;
      const originalUnits = item?.compositeRates?.originalUnits ?? 0;
      const originalVolume = item?.compositeRates?.originalVolume ?? 0;
      const totalOriginalPremium = item?.compositeRates?.totalOriginalPremium ?? 0;
      const premium01 = item?.compositeRates?.premium01 ?? 0;
      const premium02 = item?.compositeRates?.premium02 ?? 0;
      const originalPremium01 = item?.compositeRates?.originalPremium01 ?? 0;
      const originalPremium02 = item?.compositeRates?.originalPremium02 ?? 0;
      const currentOutstandingDetailId = item?.compositeRates?.currentOutstandingDetailId;
      compositeSubtotal += rowTotal;
      if (item?.compositeRates !== null) {
        const compositeCell = {
          premiumItemId: item.compositeRates?.id ?? Constants.StringEmpty,
          premiumBillId: item.compositeRates?.premiumBillId ?? Constants.StringEmpty,
          coverageType: item.coverageType ?? Constants.StringEmpty,
          class: item.classNumber ?? Constants.StringEmpty,
          numLives: numLives ?? 0,
          volume: volume,
          numLivesStr: this.decimalPipe.transform(numLives),
          volumeStr: this.decimalPipe.transform(volume, '1.2-2'),
          rate: rate,
          rateBasis: rateBasis,
          amounts: rowTotal,
          showLoader: false,
          rateUI: item?.compositeRates?.rateForUI ?? Constants.StringEmpty,
          originalUnits: originalUnits,
          originalVolume: originalVolume,
          totalOriginalPremium: totalOriginalPremium,
          premium01: premium01,
          premium02: premium02,
          originalPremium01: originalPremium01,
          originalPremium02: originalPremium02,
          currentOutstandingDetailId: currentOutstandingDetailId
        };
        compositeCells.push(compositeCell);

        const originalData = {
          premiumItemId: item.compositeRates?.id ?? Constants.StringEmpty,
          premiumBillId: item.compositeRates?.premiumBillId ?? Constants.StringEmpty,
          numLives: numLives ?? 0,
          volume: volume ?? 0,
          premiumRate01: rate ?? 0,
          premiumTotal: rowTotal,
          coverageType: item.coverageType ?? Constants.StringEmpty,
          class: item.classNumber ?? Constants.StringEmpty,
          originalUnits: originalUnits,
          originalVolume: originalVolume,
          totalOriginalPremium: totalOriginalPremium,
          premium01: premium01,
          premium02: premium01,
          originalPremium01: originalPremium01,
          originalPremium02: originalPremium02,
          currentOutstandingDetailId: currentOutstandingDetailId
        };
        this.calculationOriginal.push(originalData);
        const calculationData = JSON.parse(JSON.stringify(originalData)) as CalculationData;
        this.calculationUpdated.push(calculationData);
      }

      if (item?.bandedRates?.length) {
        const banded = this.getBandedAccordions(item.bandedRates, item.coverageType ?? '', item.classNumber ?? '');
        accordions.push({
          title: `${item.coverageType} - class ${this.formatClassNumber(item.classNumber!)}`,
          subtotal: banded.accordionTotal,
          subtotalUI: `${this.SubtotalLabel}${this.formatCurrency(`${banded.accordionTotal}`)}`,
          tableHeaders: this.TableHeaders.banded,
          cellRows: banded.result,
          showContent: false
        });
      }
    });

    accordions[0].cellRows = compositeCells;
    accordions[0].subtotal = compositeSubtotal;
    accordions[0].subtotalUI = `${this.SubtotalLabel}${this.formatCurrency(`${compositeSubtotal}`)}`;
    this.accordionData = accordions;
    this.invoiceTotal = this.formatCurrency(`${this.getCalculatedAmount(accordions)}`);

    return accordions;
  }

  getBandedAccordions(input: CoverageRateResponse[], coverageType: string, classNumber: string) {
    const result: BandedRateCellType[] = [];
    let accordionTotal = 0;

    input.map((item) => {
      const rowTotal = item.premium;
      const volume = item?.volume;
      const numLives = item?.headCount;
      const formattedNumLives = this.decimalPipe.transform(numLives);
      const formattedVolume = this.decimalPipe.transform(volume, '1.2-2');
      const originalUnits = item?.originalUnits ?? 0;
      const originalVolume = item?.originalVolume ?? 0;
      const totalOriginalPremium = item?.totalOriginalPremium ?? 0;
      const premium01 = item?.premium01 ?? 0;
      const premium02 = item?.premium02 ?? 0;
      const originalPremium01 = item?.originalPremium01 ?? 0;
      const originalPremium02 = item?.originalPremium02 ?? 0;
      const currentOutstandingDetailId = item?.currentOutstandingDetailId;
      const stepband = item.stepBand === '0-24' ? 'Under 25' : item.stepBand!.replace('-', ' to ');
      const bandedCell = {
        premiumItemId: item.id,
        premiumBillId: item.premiumBillId ?? Constants.StringEmpty,
        stepBand: stepband,
        numLives: item.headCount,
        volume: item.volume,
        numLivesStr: formattedNumLives,
        volumeStr: formattedVolume,
        rate: item.rate,
        rateBasis: item.rateBasis,
        amounts: rowTotal,
        showLoader: false,
        rateUI: item?.rateForUI ?? Constants.StringEmpty,
        originalUnits: originalUnits,
        originalVolume: originalVolume,
        totalOriginalPremium: totalOriginalPremium,
        premium01: premium01,
        premium02: premium02,
        originalPremium01: originalPremium01,
        originalPremium02: originalPremium02,
        currentOutstandingDetailId: currentOutstandingDetailId
      };
      result.push(bandedCell);
      const originalData = {
        premiumItemId: item?.id ?? Constants.StringEmpty,
        premiumBillId: item?.premiumBillId ?? Constants.StringEmpty,
        numLives: item.headCount,
        volume: item.volume,
        premiumRate01: item.rate,
        premiumTotal: rowTotal,
        coverageType: `${coverageType} - ${stepband.replace('  ', ' ').replace('  ', ' ')}`,
        class: classNumber,
        originalUnits: originalUnits,
        originalVolume: originalVolume,
        totalOriginalPremium: totalOriginalPremium,
        premium01: originalPremium01,
        premium02: originalPremium02,
        originalPremium01: originalPremium01,
        originalPremium02: originalPremium02,
        currentOutstandingDetailId: currentOutstandingDetailId
      };
      this.calculationOriginal.push(originalData);
      const calculationData = JSON.parse(JSON.stringify(originalData)) as CalculationData;
      this.calculationUpdated.push(calculationData);
    });
    accordionTotal = this.getAccordionSubTotal(result);
    return { accordionTotal, result };
  }

  getPremiumLineAmount(
    numLives?: number,
    volume?: number,
    id?: string,
    rate?: number
  ): Observable<PremiumCalculatorInfo> {
    return this.premiumClient
      .calculator({
        groupId: '',
        premiumId: this.premiumId,
        premiumDetailId: id ?? Constants.StringEmpty,
        units: numLives ?? 0,
        volume: volume ?? 0,
        premiumRate: rate ?? 0
      })
      .pipe(
        map((response) => {
          return (
            response.data ??
            ({
              groupId: '',
              premiumId: this.premiumId,
              premiumDetailId: id ?? Constants.StringEmpty,
              units: numLives ?? 0,
              volume: volume ?? 0,
              premiumTotal: 0,
              premium01: 0,
              premium02: 0
            } as PremiumCalculatorInfo)
          );
        })
      );
  }

  getAccordionSubTotal(input: CompositeRateCellType[] | BandedRateCellType[]) {
    let result = 0;
    input.forEach((item) => {
      result += item.amounts;
    });
    return result;
  }

  getCalculatedAmount(input: PremiumCalcAccordionType[]) {
    let result = 0;
    input.forEach((item) => {
      result += item.subtotal;
    });
    return result;
  }

  formatCurrency(amount: string | undefined, checkEstimated?: boolean): string {
    const currencyPipe = new CurrencyPipe('en-US');
    if (amount === Constants.StringEmpty) {
      return Constants.StringEmpty;
    } else if (checkEstimated && amount?.includes('Estimated')) {
      const num = amount.replace(/[^.0-9]/g, Constants.StringEmpty);
      const returnAmount = currencyPipe.transform(num);
      return returnAmount!;
    } else if (checkEstimated) {
      return Constants.StringEmpty;
    } else {
      return currencyPipe.transform(amount) + Constants.StringEmpty;
    }
  }

  formatNumber(input: number) {
    //convert input to a string and grab its length
    const inputString = input.toString();
    const length = inputString.length;
    let formattedNumber: string;
    //if the length is less than 3, add leading zeros
    if (length < 3) {
      switch (length) {
        case 1:
          return `0.0${inputString}`;
        case 2:
          return `0.${inputString}`;
        default:
          return `0.00`;
      }
    } else {
      //add decimal point at the tens place
      const addDecimal = inputString.replace(/(\d{2})$/, '.$1');
      // run through decimal pipe to add commas
      formattedNumber = this.decimalPipe.transform(addDecimal, '1.2-2')!;
    }
    return formattedNumber;
  }

  //adds spaces between class numbers
  formatClassNumber(input: string) {
    const formattedInput = input.split(',');
    return formattedInput.join(', ');
  }

  getStatusColor(status: string | undefined) {
    switch (status) {
      case 'Unpaid':
        return '#FDD835';
      case 'Not submitted':
        return '#FDD835';
      case 'Paid':
        return '#8ABF2E';
      case 'Submitted':
        return '#8ABF2E';
      default:
        return Constants.StringEmpty;
    }
  }

  inputValueChange(input: InputValueChangeType) {
    if (!input.inputType) return;
    //show spinner
    this.setLoader(input.tableIndex, input.accordionIndex);
    //run calculation
    const numLivesTemp = input.inputType === this.textInputType.numLivesStr ? input.event : input.item?.numLives;
    const volumeTemp = input.inputType === this.textInputType.volumeStr ? input.event : input.item?.volume;
    let numLives = numLivesTemp?.valueOf() ?? 0;
    let volume = volumeTemp?.valueOf() ?? 0;
    if (Number.isNaN(numLives)) {
      numLives = +(input.item?.numLivesStr?.replace(/,/g, '') ?? '0');
    }
    volume = this.parsePotentiallyGroupedFloat(input.item?.volumeStr ?? '0') ?? 0;
    const compositeRateCellrows = this.accordionData[input.accordionIndex].cellRows[
      input.tableIndex
    ] as CompositeRateCellType;

    this.getPremiumLineAmount(
      numLives,
      volume,
      compositeRateCellrows.premiumItemId,
      compositeRateCellrows.rate
    ).subscribe((calculator) => {
      let premium01 = 0;
      let premium02 = 0;
      let premiumTotal = 0;
      if (input.inputType === this.textInputType.numLivesStr) {
        this.accordionData[input.accordionIndex].cellRows[input.tableIndex].numLives = numLives;
      } else if (input.inputType === this.textInputType.volumeStr) {
        this.accordionData[input.accordionIndex].cellRows[input.tableIndex].volume = volume;
      }
      premium01 = calculator.premium01;
      premium02 = calculator.premium02;
      premiumTotal = calculator.premiumTotal;
      //update new total
      this.accordionData[input.accordionIndex].cellRows[input.tableIndex].amounts = premiumTotal;
      //update accordion subtotal
      const newSubtotal = this.getAccordionSubTotal(this.accordionData[input.accordionIndex].cellRows);
      this.accordionData[input.accordionIndex].subtotal = newSubtotal;
      this.accordionData[input.accordionIndex].subtotalUI =
        `${this.SubtotalLabel}${this.formatCurrency(`${newSubtotal}`)}`;
      //update page total
      this.invoiceTotal = this.formatCurrency(`${this.getCalculatedAmount(this.accordionData)}`);
      const updateddata: CalculationData = {
        premiumItemId: compositeRateCellrows.premiumItemId ?? Constants.StringEmpty,
        premiumBillId: compositeRateCellrows.premiumBillId ?? Constants.StringEmpty,
        numLives: +numLives,
        volume: +volume,
        premiumTotal: +premiumTotal,
        premiumRate01: +compositeRateCellrows.rate,
        coverageType: compositeRateCellrows.coverageType ?? Constants.StringEmpty,
        class: compositeRateCellrows.class ?? Constants.StringEmpty,
        originalUnits: compositeRateCellrows.originalUnits ?? 0,
        originalVolume: compositeRateCellrows.originalVolume ?? 0,
        totalOriginalPremium: compositeRateCellrows.totalOriginalPremium ?? 0,
        premium01: premium01 ?? 0,
        premium02: premium02 ?? 0,
        originalPremium01: compositeRateCellrows.originalPremium01 ?? 0,
        originalPremium02: compositeRateCellrows.originalPremium02 ?? 0,
        currentOutstandingDetailId: compositeRateCellrows.currentOutstandingDetailId
      };
      this.updateCalculationData(updateddata);
      //hide spinner
      this.setLoader(input.tableIndex, input.accordionIndex);
    });
  }

  setLoader(tableIndex: number, accordionIndex: number) {
    const currLoader = this.accordionData[accordionIndex].cellRows[tableIndex].showLoader;
    this.accordionData[accordionIndex].cellRows[tableIndex].showLoader = !currLoader;
  }

  goBillingList() {
    this.parent.goBillingList();
  }

  hasPermissions(input: UserContext) {
    const formattedSymClass = input?.symClass.toLowerCase();
    const isPermittedPH = formattedSymClass === 'sponsor' && input.policyholderInfo?.isAdmin === true;
    if (formattedSymClass === 'distributor' || isPermittedPH) {
      return true;
    } else {
      return false;
    }
  }

  permissionModalClose() {
    this.showPermissionModal = false;
  }

  onSubmit(input: boolean) {
    if (!input) {
      this.showPermissionModal = true;
    } else {
      const dataToUpdate = this.calculationUpdated.filter(
        (updatedOne) =>
          !this.calculationOriginal.some(
            (present) =>
              present.premiumItemId == updatedOne.premiumItemId &&
              present.numLives == updatedOne.numLives &&
              present.volume == updatedOne.volume
          )
      );
      const itemsToSubmit: PremiumItemResponse[] = dataToUpdate.map((item) => ({
        ...item,
        id: item.premiumItemId,
        premiumBillId: item.premiumBillId,
        units: item.numLives,
        volume: item.volume,
        premiumTotal: item.premiumTotal,
        premiumRate01: item.premiumRate01,
        coverageType: item.coverageType,
        class: item.class,
        originalUnits: item.originalUnits,
        originalVolume: item.originalVolume,
        totalOriginalPremium: item.totalOriginalPremium,
        premium01: item.premium01,
        premium02: item.premium02,
        originalPremium01: item.originalPremium01,
        originalPremium02: item.originalPremium02,
        currentOutstandingDetailId: item.currentOutstandingDetailId
      }));
      const totalAmount = this.calculationUpdated.reduce((sum, item) => sum + item.premiumTotal, 0);
      this.premiumClient
        .submit({
          groupId: '',
          premiumId: this.premiumId,
          premiumKey: this.premiumKey,
          groupName: this.policyService.policyName,
          groupNumber: this.policyService.groupNumber,
          billGroupName: this.groupName,
          billGroupNumber: this.groupNumber,
          invoicePeriod: this.invoicePeriod ?? this.defaultBillingPeriod,
          invoiceNumber: this.invoiceNumber,
          totalAmount: totalAmount,
          premiumItems: itemsToSubmit
        })
        .subscribe((response) => {
          if (response.messages && response.messages?.length > 0) {
            console.log('Something wrong: ' + response.messages);
          } else {
            this.submittedSuccess = true;
            this.calculationUpdated = [];
            this.billingService.setRefreshCache(true);
          }

          const displaySurvey =
            environment.environmentName !== EnvName.DEMO &&
            environment.environmentName !== EnvName.DEMODEV &&
            (this.userType?.toLowerCase() === UserRole.Sponsor || this.userType?.toLowerCase() === UserRole.Broker);

          if (displaySurvey) {
            this.qualtricsSurveyInitiate();
          }
        });
    }
  }

  qualtricsSurveyInitiate() {
    if (this.hasInterceptLoaded) {
      this.window.QSI?.API.unload();
      this.window.QSI?.API.load().then(this.window.QSI?.API.run);
    }
  }

  closeSubmittedPopup() {
    this.submittedSuccess = false;
    this.goBillingList();
  }

  updateCalculationData(newItem: CalculationData) {
    const indexToUpdate = this.calculationUpdated.findIndex((item) => item.premiumItemId === newItem.premiumItemId);
    const itemToUpdate = this.calculationUpdated[indexToUpdate];
    itemToUpdate.numLives = newItem.numLives;
    itemToUpdate.volume = newItem.volume;
    itemToUpdate.premiumTotal = newItem.premiumTotal;
    itemToUpdate.premium01 = newItem.premium01;
    itemToUpdate.premium02 = newItem.premium02;
    this.calculationUpdated[indexToUpdate] = itemToUpdate;
  }

  parsePotentiallyGroupedFloat(stringValue: string) {
    stringValue = stringValue.trim();
    let result = stringValue.replace(/[^0-9]/g, '');
    if (/[.]\d{2}$/.test(stringValue)) {
      result = result.replace(/(\d{2})$/, '.$1');
    }
    const tmpNumber = parseFloat(result);
    if (!Number.isNaN(tmpNumber)) {
      return tmpNumber;
    }
    return 0;
  }
}
